/*
 * "You may also like" row inside the theme's cart drawer.
 *
 * - Uses Shopify's free Product Recommendations API through Section Rendering
 *   (sections/cart-recommendations.liquid), so prices and images come from Liquid.
 * - Seed = the newest cart line (Shopify lists the most recently added item first).
 * - Both "complementary" (owner-curated pairings in Search & Discovery) and "related"
 *   (Shopify's automatic picks) are asked for; curated pairings come first.
 * - The theme re-renders the whole drawer on every cart change (add / qty / remove), which
 *   re-creates <cart-recommendations> with fresh data attributes. Results are cached per seed
 *   for this page view, so a re-render paints instantly from the cache.
 * - Nothing is fetched until the drawer is actually opened.
 * - "Add" posts to /cart/add.js and redraws the drawer with the theme's own renderContents(),
 *   exactly like the product page's Add to cart, so totals and the GoKwik button stay correct.
 */
(() => {
  if (customElements.get('cart-recommendations')) return;

  const MAX_SHOWN = 4;
  const FETCH_LIMIT = 10; // ask for extra so we still have 4 after removing items already in the cart
  const cache = new Map(); // seed product id -> Promise<Array<{ id, html }>>
  let lastItems = null; // previous results, shown while a new seed loads (avoids an empty flash)

  function fetchIntent(baseUrl, seed, intent) {
    const url = `${baseUrl}?section_id=cart-recommendations&product_id=${encodeURIComponent(seed)}&limit=${FETCH_LIMIT}&intent=${intent}`;
    return fetch(url)
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('.cart-recs__item')).map((li) => ({
          id: li.dataset.productId,
          html: li.outerHTML,
        }));
      })
      .catch(() => []);
  }

  function load(baseUrl, seed) {
    if (!cache.has(seed)) {
      const request = Promise.all([
        fetchIntent(baseUrl, seed, 'complementary'),
        fetchIntent(baseUrl, seed, 'related'),
      ]).then(([curated, related]) => {
        const seen = new Set();
        const merged = [...curated, ...related].filter((item) => !seen.has(item.id) && seen.add(item.id));
        if (!merged.length) cache.delete(seed); // allow a retry on the next re-render
        return merged;
      });
      cache.set(seed, request);
    }
    return cache.get(seed);
  }

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.cart-recs__list');
      this.drawer = this.closest('cart-drawer');
      this.inCart = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
      this.addEventListener('click', (event) => {
        const button = event.target.closest('.cart-recs__add');
        if (button) this.addToCart(button);
      });

      if (lastItems) this.render(lastItems);

      // Only fetch once the drawer is open (or opening), so normal page views cost nothing.
      if (!this.drawer || this.drawer.classList.contains('active')) return this.update();
      this.observer = new MutationObserver(() => {
        if (!this.drawer.classList.contains('active')) return;
        this.observer.disconnect();
        this.update();
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    update() {
      const seed = this.dataset.productId;
      if (!seed || !this.dataset.url) return;
      load(this.dataset.url, seed).then((items) => {
        if (!this.isConnected) return;
        if (items.length) lastItems = items;
        this.render(items);
      });
    }

    render(items) {
      const shown = items.filter((item) => !this.inCart.has(item.id)).slice(0, MAX_SHOWN);
      this.list.innerHTML = shown.map((item) => item.html).join('');
      this.hidden = shown.length === 0;
    }

    addToCart(button) {
      const cart = document.querySelector('cart-drawer');
      if (!cart || button.disabled) return;
      const label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        id: Number(button.dataset.variantId),
        quantity: 1,
        sections: cart.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      });

      fetch(window.routes.cart_add_url, config)
        .then((res) => res.json())
        .then((response) => {
          if (response.status) throw new Error(response.description || response.message);
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-recommendations',
              productVariantId: button.dataset.variantId,
              cartData: response,
            });
          }
          cart.renderContents(response); // the theme's own redraw: items, totals, GoKwik, cart bubble
          const live = document.getElementById('CartDrawer-LiveRegionText');
          if (live) live.textContent = `${button.dataset.title} added to cart`;
        })
        .catch((error) => {
          console.error(error);
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = label;
          const live = document.getElementById('CartDrawer-LiveRegionText');
          if (live) live.textContent = (window.cartStrings && window.cartStrings.error) || 'Could not add to cart';
        });
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
